import { AxiosPromise } from 'axios';

import { MessengerMessageType } from '~components/business-owner/messenger/constants';

import httpClient from '~api/httpClient';
import {
	MessengerConversationItem,
	MessengerConversationsCountRequestParamsType,
	MessengerConversationsCountType,
	MessengerMessage,
	MessengerMessageLinkMetadata,
	MessengerMessageMediaAttachment,
} from '~types/messenger';

import {
	FetchBusinessConfigurationResponse,
	FetchChatBotEnabledResponse,
	FetchConversationMessagesParams,
	FetchConversationMessagesResponse,
	FetchConversationsParams,
	FetchConversationsResponse,
	FetchMessengerConnectionUrlResponse,
	MessengerUpdateConversationsReadStatusRequestBody,
	SendBulkMessageResponse,
	UpdateBusinessConfigurationBody,
	UpdateConfigurationChatBotEnabledBody,
	UpdateConversationChatBotEnabledBody,
} from './types';

export const fetchMessengerConnectionUrl =
	(): AxiosPromise<FetchMessengerConnectionUrlResponse> => {
		return httpClient({
			method: 'GET',
			url: 'websocket-connection/messenger',
		});
	};

export const fetchConversation = (
	centsCustomerId: number,
): AxiosPromise<MessengerConversationItem> => {
	return httpClient({
		method: 'GET',
		url: `messenger/conversations/${centsCustomerId}/details`,
	});
};

export const fetchConversations = ({
	params,
}: {
	params: FetchConversationsParams;
}): AxiosPromise<FetchConversationsResponse> => {
	return httpClient({
		method: 'GET',
		url: 'messenger/conversations',
		params,
	});
};

export const fetchChatBotEnabled = (): AxiosPromise<FetchChatBotEnabledResponse> => {
	return httpClient({
		method: 'GET',
		url: `messenger/configuration/chat-bot-enabled`,
	});
};

export const fetchBusinessConfiguration =
	(): AxiosPromise<FetchBusinessConfigurationResponse> => {
		return httpClient({
			method: 'GET',
			url: `messenger/configuration`,
		});
	};

export const updateBusinessConfiguration = (
	body: UpdateBusinessConfigurationBody,
): AxiosPromise<FetchBusinessConfigurationResponse> => {
	return httpClient({
		method: 'PATCH',
		url: `messenger/configuration`,
		data: body,
	});
};

export const updateConfigurationChatBotEnabled = (
	body: UpdateConfigurationChatBotEnabledBody,
): AxiosPromise<FetchChatBotEnabledResponse> => {
	return httpClient({
		method: 'PATCH',
		url: `messenger/configuration/chat-bot-enabled`,
		data: body,
	});
};

export const updateConversationChatBotEnabled = (
	body: UpdateConversationChatBotEnabledBody,
): AxiosPromise<FetchChatBotEnabledResponse> => {
	return httpClient({
		method: 'PATCH',
		url: `messenger/conversations/chat-bot-enabled`,
		data: body,
	});
};

export const fetchUnreadConversationsCount = (
	params?: MessengerConversationsCountRequestParamsType,
): AxiosPromise<MessengerConversationsCountType> => {
	return httpClient({
		params,
		method: 'GET',
		url: 'messenger/conversations/unread-count',
	});
};

export const fetchEscalationCount = (): AxiosPromise<MessengerConversationsCountType> => {
	return httpClient({
		method: 'GET',
		url: 'messenger/conversations/escalations/count',
	});
};

export const fetchConversationMessages = (
	centsCustomerId: string,
	params: FetchConversationMessagesParams,
): AxiosPromise<FetchConversationMessagesResponse> => {
	return httpClient({
		method: 'GET',
		url: `messenger/conversations/${centsCustomerId}`,
		params,
	});
};

export const markConversationAsRead = (
	centsCustomerId: string,
	data?: {
		messageType?: MessengerMessageType;
	},
): AxiosPromise<MessengerConversationItem> => {
	return httpClient({
		data,
		method: 'PATCH',
		url: `messenger/conversations/${centsCustomerId}/read`,
	});
};

export const markConversationAsResolved = (
	centsCustomerId: number | string,
): AxiosPromise<MessengerConversationItem> => {
	return httpClient({
		method: 'PATCH',
		url: `messenger/conversations/${centsCustomerId}/resolve`,
	});
};

export const sendSmsMessage = (centsCustomerId: string, body: FormData): AxiosPromise => {
	return httpClient({
		method: 'POST',
		url: `messenger/conversations/${centsCustomerId}/message`,
		data: body,
	});
};

export const sendBulkMessage = (
	body: FormData,
): AxiosPromise<SendBulkMessageResponse> => {
	return httpClient({
		method: 'POST',
		url: 'messenger/conversations/bulk-message',
		data: body,
	});
};

export const updateConversationsReadStatus = (
	body: MessengerUpdateConversationsReadStatusRequestBody,
): AxiosPromise<void> => {
	return httpClient.post('messenger/conversations/read-status', body);
};

export const fetchMessageMedia = (
	messageId: MessengerMessage['messageId'],
): AxiosPromise<MessengerMessageMediaAttachment[]> => {
	return httpClient({
		method: 'GET',
		url: `messenger/messages/${messageId}/media`,
	});
};

export const fetchMessageLinkMetadata = (
	url: string,
): AxiosPromise<MessengerMessageLinkMetadata> => {
	return httpClient.get('messenger/messages/link-metadata', {
		params: {
			url,
		},
	});
};

export const removeMessage = ({
	businessId,
	customerId,
	deliveryStatus,
	messageId,
}: MessengerMessage): AxiosPromise => {
	return httpClient.post('messenger/messages/delete', {
		businessId,
		customerId,
		deliveryStatus,
		messageId,
	});
};

/**
 * Deletes every message for the given customers in one call.
 *
 * Same endpoint as `removeMessage`, discriminated by body: `customerId` plus
 * `messageId` removes a single message, while `customerIds` wipes whole
 * conversations server-side.
 */
export const removeCustomerMessages = ({
	businessId,
	customerIds,
}: {
	businessId: number;
	customerIds: number[];
}): AxiosPromise => {
	return httpClient.post('messenger/messages/delete', {
		businessId,
		customerIds,
	});
};

export const resendMessage = ({
	message: { businessId, customerId, deliveryStatus, messageId },
	messageType,
}: {
	message: MessengerMessage;
	messageType?: MessengerMessageType;
}): AxiosPromise => {
	return httpClient.post('messenger/messages/resend', {
		businessId,
		customerId,
		deliveryStatus,
		messageId,
		messageType,
	});
};

export const translateMessage = (
	messageId: string,
	content: string,
): AxiosPromise<string> => {
	return httpClient.post<string>(`messenger/messages/${messageId}/translate`, {
		content,
	});
};
